import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, apiPostJson } from "@/lib/api";
import { useAuthUser } from "@/lib/auth";

// EmailVerificationBanner — site-wide reminder for users whose email hasn't
// been verified yet. Renders nothing when:
//   - user is unauthenticated (no banner needed for guests)
//   - email_verified is true (the verified pill is already shown elsewhere)
//   - user dismissed it for this session (state-only, intentional — we want
//     to nag again on a fresh tab)
//
// Network behaviour: clicking "Resend" POSTs to /auth/verify/send. The
// backend has its own 60-second flood guard plus per-IP rate limit, so a
// rapid double-click can come back as 429; we surface that as an error.
export function EmailVerificationBanner() {
  const { user } = useAuthUser();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [dismissed, setDismissed] = useState(false);

  if (!user) return null;
  if (user.email_verified) return null;
  if (dismissed) return null;

  async function resend() {
    setBusy(true);
    setErr("");
    try {
      await apiPostJson("/auth/verify/send", {});
      setSent(true);
    } catch (e) {
      const ae = e as ApiError;
      if (ae.status === 429) setErr(t("auth.errorRateLimit"));
      else setErr(ae.message || t("auth.errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  // Narrow-viewport behaviour: long-email + i18n template can easily push
  // a single-line `truncate` past its container on ≤ 360px screens, hiding
  // the email entirely. Below `sm` we allow up to 2 lines (line-clamp-2);
  // at `sm+` we keep the original single-line truncate for density.
  // The `title` attribute on the message div exposes the full text on
  // hover (desktop) and to assistive tech, so even when truncated the
  // user can still discover the actual address.
  const messageTitle = !sent && !err ? user.email : undefined;
  return (
    <div
      className="flex items-start gap-2.5 border-b border-yellow-500/30 bg-yellow-500/10 px-4 py-1.5 text-[12px] text-text-sec sm:items-center"
      role="status"
    >
      <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-yellow-500/20 text-[9.5px] font-bold text-yellow-600 sm:mt-0">
        !
      </span>
      <div
        className="min-w-0 flex-1 line-clamp-2 sm:truncate"
        title={messageTitle}
      >
        {sent ? (
          <span className="text-text-sec">{t("auth.verify.sent")}</span>
        ) : err ? (
          <span className="text-red-500">{err}</span>
        ) : (
          <span>{t("auth.verify.banner", { email: user.email })}</span>
        )}
      </div>
      {!sent && !err && (
        <button
          onClick={() => void resend()}
          disabled={busy}
          className="shrink-0 rounded border border-yellow-500/40 bg-yellow-500/10 px-2 py-0.5 text-[11px] font-medium text-yellow-700 transition hover:bg-yellow-500/20 disabled:opacity-50"
        >
          {busy ? t("auth.verify.resending") : t("auth.verify.resend")}
        </button>
      )}
      <button
        onClick={() => setDismissed(true)}
        aria-label={t("common.close")}
        className="shrink-0 rounded p-1 text-text-muted transition hover:bg-yellow-500/15 hover:text-text"
      >
        <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
          <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
